import {
  ApiTokenListResponse,
  AppointmentCustomerDTO,
  AppointmentDTO,
  AppointmentPurchaseSummaryDTO,
  AppointmentSummaryAsMemberPostDTO,
  InvoiceDTO,
  MemberAppointmentPostDTO,
  TokenPageableRequestParams,
} from "@gymflow/types";
import {
  infiniteQueryOptions,
  mutationOptions,
  QueryKey,
  queryOptions,
} from "@tanstack/react-query";

import { api } from "../../api";
import { queryClient } from "../../QueryClient";
import { tokenPagination } from "../../tokenPagination";
import { memberApiAccess } from "./memberApiAccess";
import { memberApiMember } from "./memberApiMember";

const keys = {
  all: () => ["memberApiAppointment"] as QueryKey,
  perUser: (userId: unknown) => [...keys.all(), "userId", userId] as QueryKey,
  id: (userId: unknown, id: unknown) =>
    [...keys.perUser(userId), "id", id] as QueryKey,
  summary: (userId: unknown, params: unknown) =>
    [...keys.perUser(userId), "summary", params] as QueryKey,
  upcoming: (userId: unknown, params: unknown) =>
    [...keys.perUser(userId), "upcoming", params] as QueryKey,
  past: (userId: unknown, params: unknown) =>
    [...keys.perUser(userId), "past", params] as QueryKey,
};

export const memberApiAppointment = {
  keys,
  queries: {
    id: (userId: string | undefined, id: number | undefined) =>
      queryOptions({
        queryKey: keys.id(userId, id),
        queryFn: async () => {
          return (await api.get<AppointmentDTO>(`/customer/appointment/${id}`))
            .data;
        },
        enabled: !!userId && !!id,
      }),
    summary: (
      userId: string | undefined,
      params: AppointmentSummaryAsMemberPostDTO | undefined,
    ) =>
      queryOptions({
        queryKey: keys.summary(userId, params),
        queryFn: async () => {
          return (
            await api.post<AppointmentPurchaseSummaryDTO>(
              `/customer/appointment/summary`,
              params,
            )
          ).data;
        },
        enabled: !!userId && !!params,
      }),
  },
  infiniteQueries: {
    upcoming: (
      memberId: string | undefined,
      params: {
        appointableId?: number[];
        appointmentHostId?: string[];
        appointableCategoryId?: number[];
        appointmentFacilityId?: number[];
      } & TokenPageableRequestParams,
    ) =>
      infiniteQueryOptions({
        queryKey: keys.upcoming(memberId, params),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<AppointmentCustomerDTO>>(
              `/customer/appointment/upcoming`,
              {
                params: {
                  ...params,
                  nextPageToken: pageParam,
                },
              },
            )
          ).data;
        },
        enabled: memberId !== undefined,
        ...tokenPagination,
      }),
    past: (
      memberId: string | undefined,
      params: {
        appointableId?: number[];
        appointmentHostId?: string[];
        appointableCategoryId?: number[];
        appointmentFacilityId?: number[];
      } & TokenPageableRequestParams,
    ) =>
      infiniteQueryOptions({
        queryKey: keys.past(memberId, params),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<AppointmentCustomerDTO>>(
              `/customer/appointment/past`,
              {
                params: {
                  ...params,
                  nextPageToken: pageParam,
                },
              },
            )
          ).data;
        },
        enabled: memberId !== undefined,
        ...tokenPagination,
      }),
  },
  mutations: {
    create: () =>
      mutationOptions({
        mutationFn: async (newResource: {
          userId: string | undefined;
          appointment: MemberAppointmentPostDTO;
        }) => {
          return (
            await api.post<{
              appointment: AppointmentDTO;
              invoice: InvoiceDTO;
            }>(`/customer/appointment`, newResource.appointment)
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({
            queryKey: keys.perUser(userId),
          });
          await queryClient.invalidateQueries({
            queryKey: memberApiMember.keys.id(userId),
          });
          await queryClient.invalidateQueries({
            queryKey: memberApiAccess.keys.perUser(userId),
          });
        },
      }),
    update: () =>
      mutationOptions({
        mutationFn: async ({
          appointmentId,
          appointment,
        }: {
          userId: string | undefined;
          appointmentId: number;
          appointment: Partial<MemberAppointmentPostDTO>;
        }) => {
          return (
            await api.patch(
              `/customer/appointment/${appointmentId}`,
              appointment,
            )
          ).data;
        },
        onSettled: async (_, __, { userId, appointmentId }) => {
          await queryClient.invalidateQueries({
            queryKey: keys.id(userId, appointmentId),
          });
          await queryClient.invalidateQueries({
            queryKey: memberApiMember.keys.id(userId),
          });
          await queryClient.invalidateQueries({
            queryKey: memberApiAccess.keys.perUser(userId),
          });
        },
      }),
    lateCancel: () =>
      mutationOptions({
        mutationFn: async ({
          id,
          paymentMethodId,
        }: {
          id: number;
          userId: string;
          paymentMethodId?: string;
        }) => {
          return (
            await api.put<{
              invoiceNumber?: string;
            }>(`/customer/appointment/${id}/late-cancel`, {
              paymentMethodId,
            })
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await queryClient.invalidateQueries({
            queryKey: keys.perUser(userId),
          });
          await queryClient.invalidateQueries({
            queryKey: memberApiMember.keys.id(userId),
          });
          await queryClient.invalidateQueries({
            queryKey: memberApiAccess.keys.perUser(userId),
          });
        },
      }),
  },
};
